/**
 * Admin → Edge Provisioning: what a docker scanner is pointed at.
 *
 * A scanner for the data stores an organisation named reads them from
 * scan-targets.json on its own host. This panel builds that file: the
 * administrator gives the store list (the scan list FOCTTA Consent's Data
 * Connections page offers, or one written by hand) and, for each database,
 * the output of one metadata query. The controller answers with the file, a
 * count per store, and every column it left out with the reason.
 *
 * No password is asked for here and none can be given: the store list is
 * refused if it carries one, and the installer's set-store-passwords.sh asks
 * for each on the scanner's host.
 *
 * Backend:
 *   POST /v1/edge/scanner-setup/target-file
 */
import { useRef, useState } from 'react';
import { AlertTriangle, Check, Download, FileUp, Loader2 } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ApiError, buildScannerTargetFile, type ScanListStore, type ScannerTargetFile } from '@/lib/api';

export type ScanTargetsMode = 'host_folders' | 'target_file';

/** SQL Server is swept whole by the scanner: no column list is asked for. */
const NO_COLUMN_LIST = new Set(['mssql']);

/**
 * Engines whose scanner checks for tables and columns the column list does not name (release 3.6 item 26).
 * PostgreSQL, MySQL and Oracle scan them; MongoDB counts and reports new collections and fields but does not
 * scan them (review of #190, round 7): on MongoDB only listed fields are scanned.
 */
const CAN_PICK_UP_NEW = new Set(['postgres', 'mysql', 'oracle', 'mongodb']);

const ENGINE_LABEL: Record<string, string> = {
  postgres: 'PostgreSQL',
  mysql: 'MySQL',
  oracle: 'Oracle',
  mssql: 'SQL Server',
  mongodb: 'MongoDB',
};

/**
 * The metadata query whose output is a store's column list. Names of columns
 * only: no row of the organisation's data is read. Export it as CSV with the
 * header row.
 */
export const COLUMN_LIST_QUERY: Record<string, string> = {
  postgres:
    "SELECT table_schema, table_name, column_name, data_type FROM information_schema.columns WHERE table_schema NOT IN ('pg_catalog', 'information_schema') ORDER BY 1, 2, ordinal_position;",
  mysql:
    "SELECT table_schema AS table_schema, table_name AS table_name, column_name AS column_name, data_type AS data_type FROM information_schema.columns WHERE table_schema NOT IN ('mysql', 'sys', 'information_schema', 'performance_schema') ORDER BY 1, 2, ordinal_position;",
  oracle:
    "SELECT owner, table_name, column_name, data_type FROM all_tab_columns WHERE owner NOT IN (SELECT username FROM all_users WHERE oracle_maintained = 'Y') ORDER BY 1, 2, column_id;",
  mongodb: 'A CSV with the header database,collection,field and one row for each field to scan.',
};

function whereOf(store: ScanListStore): string {
  const host = String(store.host ?? '');
  return typeof store.port === 'number' ? `${host}:${store.port}` : host;
}

function saveAs(name: string, contents: string, type = 'application/json'): void {
  const url = URL.createObjectURL(new Blob([contents], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

export function ScannerTargetsPanel(props: {
  mode: ScanTargetsMode;
  onModeChange: (mode: ScanTargetsMode) => void;
  built: ScannerTargetFile | null;
  onBuilt: (built: ScannerTargetFile | null) => void;
}) {
  const { mode, onModeChange, built, onBuilt } = props;
  const [stores, setStores] = useState<ScanListStore[] | null>(null);
  // The scan list as read, so it can be given back with the pickup choices written in.
  const [listDoc, setListDoc] = useState<Record<string, unknown> | null>(null);
  const [columnLists, setColumnLists] = useState<Record<string, { name: string; text: string }>>({});
  const [listName, setListName] = useState<string | null>(null);
  const [pickUp, setPickUp] = useState<Record<string, boolean>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const listTurn = useRef(0);

  async function readStoreList(file: File | undefined): Promise<void> {
    if (!file) return;
    listTurn.current += 1;
    const turn = listTurn.current;
    setProblem(null);
    onBuilt(null);
    setColumnLists({});
    setPickUp({});
    setListName(null);
    try {
      const text = await file.text();
      // A list picked after this one, and read sooner, is the one that stands.
      if (turn !== listTurn.current) return;
      const doc = JSON.parse(text) as { stores?: unknown };
      const list = Array.isArray(doc?.stores) ? (doc.stores as ScanListStore[]) : null;
      if (!list || list.length === 0 || list.some((s) => typeof s?.id !== 'string' || typeof s?.engine !== 'string')) {
        setStores(null);
        setProblem(`${file.name} is not a scan list: it needs a "stores" list, each with an id and an engine.`);
        return;
      }
      setListDoc(doc as Record<string, unknown>);
      setStores(list);
      setListName(file.name);
    } catch {
      setStores(null);
      setProblem(`${file.name} could not be read as JSON.`);
    }
  }

  async function readColumnList(storeId: string, file: File | undefined): Promise<void> {
    if (!file) return;
    onBuilt(null);
    const forList = listTurn.current;
    const text = await file.text();
    // A scan list picked while this was being read is a different list.
    if (forList !== listTurn.current) return;
    setColumnLists((prev) => ({ ...prev, [storeId]: { name: file.name, text } }));
  }

  const needColumnList = (stores ?? []).filter((s) => !NO_COLUMN_LIST.has(s.engine));
  const listOf = (id: string): { name: string; text: string } | undefined =>
    Object.hasOwn(columnLists, id) ? columnLists[id] : undefined;
  const missing = needColumnList.filter((s) => !listOf(s.id));
  // A list that already says so starts ticked; the box overrides it either way.
  const picksUpNew = (s: ScanListStore): boolean =>
    CAN_PICK_UP_NEW.has(s.engine) && (Object.hasOwn(pickUp, s.id) ? pickUp[s.id] === true : s.pick_up_new_objects === true);

  // The stores with the boxes as ticked. Only the engines that can do it are rewritten: for the others a
  // list that asks for it is sent as it is, and the controller answers with its refusal instead of the
  // page dropping the request.
  const withChoices = (list: ScanListStore[]): ScanListStore[] =>
    list.map((s) => {
      if (!CAN_PICK_UP_NEW.has(s.engine)) return s;
      const { pick_up_new_objects: _listed, ...rest } = s;
      return picksUpNew(s) ? { ...rest, pick_up_new_objects: true } : rest;
    });
  // Review of #190, round 12: the box is not saved anywhere but the file built now. A store whose box
  // differs from its scan list goes back to the list's setting the next time a file is built from that
  // list, so the page says so and offers the list with the choice written in.
  const notInList = (stores ?? []).filter(
    (s) => CAN_PICK_UP_NEW.has(s.engine) && picksUpNew(s) !== (s.pick_up_new_objects === true),
  );

  async function build(): Promise<void> {
    if (!stores) return;
    setBusy(true);
    setProblem(null);
    try {
      const catalogues = Object.fromEntries(Object.entries(columnLists).map(([id, c]) => [id, c.text]));
      onBuilt(await buildScannerTargetFile({ stores: withChoices(stores), catalogues }));
    } catch (e) {
      onBuilt(null);
      // A refusal is the tool's own sentence: it names the store and the field.
      const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
      setProblem(body?.message ?? (e instanceof Error ? e.message : 'The target file could not be built.'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <GlassCard className="p-5">
      <CardHeader className="p-0 mb-3">
        <CardTitle>What will it scan?</CardTitle>
        <CardDescription clamp>
          Folders on the machine it runs on, or the databases you named.
        </CardDescription>
      </CardHeader>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3" role="radiogroup" aria-label="What the scanner scans">
        {(
          [
            ['host_folders', 'Folders on its host', 'The folder you mount at ./data.'],
            ['target_file', 'Databases you named', 'PostgreSQL, MySQL, Oracle, SQL Server and MongoDB, reached over your network.'],
          ] as const
        ).map(([value, label, helper]) => (
          <button
            key={value}
            type="button"
            role="radio"
            aria-checked={mode === value}
            onClick={() => onModeChange(value)}
            className={`text-left p-3 rounded-md border transition-colors ${
              mode === value ? 'border-primary bg-primary/10' : 'border-border/30 glass-lite hover:bg-muted/30'
            }`}
          >
            <div className="text-sm font-medium">{label}</div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{helper}</div>
          </button>
        ))}
      </div>

      {mode === 'target_file' && (
        <div className="mt-4 space-y-4">
          <div>
            <label htmlFor="scan-list-file" className="text-xs font-medium flex items-center gap-1.5">
              <FileUp className="h-3.5 w-3.5" /> Scan list (stores.json)
            </label>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              FOCTTA Consent offers it on Data Connections as the scan list. It names each store and where it is, and
              holds no password.
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">
              The scanner signs in to each database as <span className="font-mono">foctta_scanner</span>, a read-only
              account you create there. A store that uses another account names it in the list as{' '}
              <span className="font-mono">username</span>.
            </p>
            <input
              id="scan-list-file"
              type="file"
              disabled={busy}
              accept=".json,application/json"
              className="mt-2 block text-xs"
              onChange={(e) => {
                const file = e.target.files?.[0];
                // Emptied so the same file, corrected and picked again, is read again.
                e.target.value = '';
                void readStoreList(file);
              }}
            />
            {listName && (
              <div className="text-[11px] text-success mt-1 flex items-center gap-1">
                <Check className="h-3 w-3" /> {listName}
              </div>
            )}
          </div>

          {stores && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="py-1 pr-3 font-medium">Store</th>
                    <th className="py-1 pr-3 font-medium">Kind</th>
                    <th className="py-1 pr-3 font-medium">Where</th>
                    <th className="py-1 pr-3 font-medium">Column list</th>
                    <th className="py-1 font-medium">New tables and columns</th>
                  </tr>
                </thead>
                <tbody>
                  {stores.map((s) => (
                    <tr key={s.id} className="border-t border-border/20 align-top">
                      <td className="py-2 pr-3 font-mono">{s.id}</td>
                      <td className="py-2 pr-3">{ENGINE_LABEL[s.engine] ?? s.engine}</td>
                      <td className="py-2 pr-3 font-mono break-all">{whereOf(s)}</td>
                      <td className="py-2">
                        {NO_COLUMN_LIST.has(s.engine) ? (
                          <span className="text-muted-foreground">Not needed: the scanner sweeps it.</span>
                        ) : (
                          <div>
                            <input
                              type="file"
                              disabled={busy}
                              accept=".csv,.tsv,.txt,text/csv,text/plain"
                              aria-label={`Column list for ${s.id}`}
                              className="block text-xs"
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = '';
                                void readColumnList(s.id, file);
                              }}
                            />
                            {listOf(s.id) && (
                              <div className="text-[11px] text-success mt-1 flex items-center gap-1">
                                <Check className="h-3 w-3" /> {listOf(s.id)?.name}
                              </div>
                            )}
                            {!listOf(s.id) && s.engine === 'mongodb' && (
                              <p className="text-[11px] text-muted-foreground mt-1">{COLUMN_LIST_QUERY.mongodb}</p>
                            )}
                            {!listOf(s.id) && s.engine !== 'mongodb' && (
                              <details className="mt-1">
                                <summary className="text-[11px] text-muted-foreground cursor-pointer">
                                  How to get it
                                </summary>
                                <p className="text-[11px] text-muted-foreground mt-1">
                                  Run this on the store as the scanner&rsquo;s own account, so it lists what the
                                  scanner can read, and save the output as CSV with its header row. It reads column
                                  names only, never a row of data.
                                </p>
                                <pre className="glass-lite rounded p-2 mt-1 text-[10px] font-mono whitespace-pre-wrap break-all">
                                  {COLUMN_LIST_QUERY[s.engine] ?? ''}
                                </pre>
                              </details>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="py-2">
                        {CAN_PICK_UP_NEW.has(s.engine) ? (
                          <label className="flex items-start gap-1.5">
                            <input
                              type="checkbox"
                              disabled={busy}
                              checked={picksUpNew(s)}
                              aria-label={
                                s.engine === 'mongodb'
                                  ? `Report new collections and fields of ${s.id}`
                                  : `Pick up new tables and columns of ${s.id}`
                              }
                              onChange={(e) => {
                                onBuilt(null);
                                setPickUp((prev) => ({ ...prev, [s.id]: e.target.checked }));
                              }}
                            />
                            {s.engine === 'mongodb' ? (
                              <span className="text-[11px] text-muted-foreground">
                                Report them, do not scan them. On MongoDB only the fields in the column list are
                                scanned. Each cycle every collection is listed and the shapes of a sample of its
                                documents are read (key names and types, never values), and fields the list does
                                not name are counted: the run reads as partly covered until you add them to the
                                list. No new collection or field name is shown, logged or sent, only counts.
                                Collections named like data (a mobile number, an email) are not read. Needs
                                MongoDB 4.2 or later; DocumentDB and Cosmos DB are untested.
                              </span>
                            ) : (
                              <span className="text-[11px] text-muted-foreground">
                                Scan them as they appear. Every column in the schema is scanned each cycle, not
                                only the ones listed. A table the scanner&rsquo;s account cannot read is reported
                                as a failed target, never skipped. Views, materialized views and foreign tables
                                are not scanned. A new table or column whose name looks like data (such as
                                cust_9000000001, or an email) is not scanned either: it is counted and the run
                                reads as partly covered. A name that is data written as words cannot be told
                                apart from an ordinary name.
                                {s.engine === 'oracle'
                                  ? ' On Oracle this is automatic only when the scanner\u2019s account holds READ ANY TABLE ' +
                                    '(on Oracle 23ai, READ ANY TABLE ON SCHEMA for this schema alone is enough): ' +
                                    'Oracle has no default grants, so with per-table grants every new table is reported ' +
                                    'as a failed target until a DBA grants it. Grant SELECT_CATALOG_ROLE too, so tables ' +
                                    'Oracle makes for itself (queue and Flashback Data Archive tables) are confirmed as ' +
                                    'Oracle\u2019s own, and columns under Data Redaction are counted; without it they ' +
                                    'are counted every run as not scanned, and redaction cannot be checked.'
                                  : null}
                              </span>
                            )}
                          </label>
                        ) : s.engine === 'mssql' ? (
                          <span className="text-muted-foreground">Always: the scanner sweeps it.</span>
                        ) : (
                          <span className="text-muted-foreground">Not yet for this engine.</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {stores && notInList.length > 0 && (
            <div role="status" className="text-[11px] flex flex-wrap items-center gap-2" data-testid="pickup-not-in-list">
              <span>
                Your scan list ({listName ?? 'stores.json'}) does not record the new tables and columns choice for{' '}
                {notInList.map((s) => s.id).join(', ')}. The box is kept only in the file you build now: a file
                built again from the old list goes back to what the list says. Save the scan list with this choice
                and use that copy next time.
              </span>
              <Button
                size="sm"
                variant="glass"
                disabled={busy}
                onClick={() =>
                  saveAs(listName ?? 'stores.json', `${JSON.stringify({ ...listDoc, stores: withChoices(stores) }, null, 2)}\n`)
                }
              >
                <Download className="h-3.5 w-3.5 mr-1" /> Scan list with this choice
              </Button>
            </div>
          )}

          {problem && (
            <div role="alert" className="text-xs text-destructive flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>{problem}</span>
            </div>
          )}

          {stores && !built && (
            <div className="flex items-center gap-3">
              <Button size="sm" disabled={busy || missing.length > 0} onClick={() => void build()}>
                {busy ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : null}
                Build the target file
              </Button>
              {missing.length > 0 && (
                <span className="text-[11px] text-muted-foreground">
                  Waiting for the column list of {missing.map((s) => s.id).join(', ')}.
                </span>
              )}
            </div>
          )}

          {built && <TargetFileSummary built={built} />}
        </div>
      )}
    </GlassCard>
  );
}

/** What was built: counts per store, what was left out, and the files. Shown again after the token is minted. */
export function TargetFileSummary({ built }: { built: ScannerTargetFile }) {
  return (
    <div className="glass-lite rounded-md p-3 space-y-3" data-testid="target-file-summary">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium">
          scan-targets.json: {built.targets} {built.targets === 1 ? 'target' : 'targets'} in {built.stores.length}{' '}
          {built.stores.length === 1 ? 'store' : 'stores'}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="glass" onClick={() => saveAs(built.fileName, built.contents)}>
            <Download className="h-3.5 w-3.5 mr-1" /> scan-targets.json
          </Button>
          {built.notCovered.total > 0 && (
            <Button size="sm" variant="glass" onClick={() => saveAs('not-covered.csv', built.notCovered.csv, 'text/csv')}>
              <Download className="h-3.5 w-3.5 mr-1" /> not-covered.csv
            </Button>
          )}
        </div>
      </div>
      <ul className="text-xs space-y-0.5">
        {built.stores.map((s) => (
          <li key={s.store}>
            <span className="font-mono">{s.store}</span>: {s.targets} {s.targets === 1 ? 'target' : 'targets'}
            {s.notCovered > 0 ? `, ${s.notCovered} not covered` : ''}
            {s.pickUpNewObjects
              ? s.engine === 'mongodb'
                ? ', reports new collections and fields (not scanned)'
                : ', picks up new tables and columns'
              : ''}
          </li>
        ))}
      </ul>
      {built.notCovered.total > 0 && (
        <details>
          <summary className="text-xs cursor-pointer">
            {built.notCovered.total} {built.notCovered.total === 1 ? 'column is' : 'columns are'} not covered
          </summary>
          <ul className="text-[11px] text-muted-foreground mt-1 space-y-0.5 max-h-40 overflow-y-auto">
            {built.notCovered.shown.map((n) => (
              <li key={`${n.store}:${n.location}:${n.line}`}>
                <span className="font-mono">{n.store} {n.location}</span>: {n.reason}
              </li>
            ))}
            {built.notCovered.total > built.notCovered.shown.length && (
              <li>The rest are in not-covered.csv.</li>
            )}
          </ul>
        </details>
      )}
      <p className="text-[11px] text-muted-foreground">
        {built.passwords.length > 0
          ? `The file holds a placeholder where each password goes (${built.passwords.map((p) => p.store).join(', ')}). The installer's set-store-passwords.sh asks for them on the scanner's host; they never reach FOCTTA.`
          : 'No password is needed for these stores.'}
      </p>
    </div>
  );
}
